import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useUserProfileStore } from '../stores/userProfile'

const routes = [
  {
    path: '/',
    name: 'Landing',
    component: () => import('../views/LandingView.vue'),
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/LoginView.vue'),
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/ForgotPasswordView.vue'),
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('../views/TermsView.vue'),
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('../views/PrivacyView.vue'),
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPasswordView.vue'),
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: () => import('../views/OnboardingView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/locations',
    name: 'Locations',
    component: () => import('../views/LocationsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/airports',
    name: 'Airports',
    component: () => import('../views/AirportConfigView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/airports/:id',
    name: 'AirportConfigDetail',
    component: () => import('../views/AirportConfigDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/coach-partners',
    name: 'CoachPartners',
    component: () => import('../views/CoachPartnersView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/coach-partners/:id',
    name: 'CoachPartnerDetail',
    component: () => import('../views/CoachPartnerDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/flight-monitor',
    name: 'FlightMonitor',
    component: () => import('../views/FlightMonitorView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/account',
    name: 'Account',
    component: () => import('../views/AccountView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/support',
    name: 'Support',
    component: () => import('../views/SupportView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/plans/:id',
    name: 'PlanDetail',
    component: () => import('../views/PlanDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/driver-sheet/:token',
    name: 'DriverSheet',
    component: () => import('../views/DriverSheetView.vue'),
  },
  {
    path: '/day-plan-sheet/:token',
    name: 'DayPlanSheet',
    component: () => import('../views/DayPlanSheetView.vue'),
  },
  {
    path: '/partner-link/:token',
    name: 'PartnerLink',
    component: () => import('../views/PartnerLinkView.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFoundView.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

router.beforeEach((to) => {
  const auth = useAuthStore()
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'Login' }
  }
  // Redirect authenticated users away from landing/login/forgot-password to dashboard
  // Note: ResetPassword is excluded — Supabase recovery links establish a session before landing
  if (auth.isAuthenticated && (to.name === 'Landing' || to.name === 'Login' || to.name === 'ForgotPassword')) {
    return { name: 'Dashboard' }
  }

  // Profile-based guards (only when profile store has loaded and no fetch error)
  if (auth.isAuthenticated) {
    const profileStore = useUserProfileStore()

    if (profileStore.loaded && !profileStore.fetchError) {
      // Redirect to onboarding if profile not completed
      if (!profileStore.onboardingComplete && to.name !== 'Onboarding') {
        return { name: 'Onboarding' }
      }
      // Prevent re-entering onboarding
      if (profileStore.onboardingComplete && to.name === 'Onboarding') {
        return { name: 'Dashboard' }
      }
      // Guard partner routes for own-fleet users
      if (!profileStore.usesExternalPartners && to.path.startsWith('/coach-partners')) {
        return { name: 'Dashboard' }
      }
    }
  }
})

export default router
